import {
  usePhotoVerificationPpeEyeProtectionEnabled,
  usePhotoVerificationPpeFaceCoverEnabled,
  usePhotoVerificationPpeHeadCoverEnabled,
  usePhotoVerificationPpeSafetyVestEnabled,
} from 'containers/photo-verification-report/hooks/usePhotoVerificationPpeFeatureFlags';
import { t } from 'i18next';
import { useCallback } from 'react';
import IPosition from 'types/Position';
import {
  defaultPositionPPESettings,
  getPhotoVerificationSettings,
  IPhotoVerificationPPESettings,
  PositionPhotoVerificationPPESettingsScanType,
  PositionPhotoVerificationSettingsActionType,
} from 'utils/positionUtils';

export enum PhotoVerificationOptionType {
  KIOSK_ONLY = 'kioskOnly',
  POSITION = 'position',
}

export const photoVerificationSettingsFormAllActions = [
  PositionPhotoVerificationSettingsActionType.CLOCK_IN,
  PositionPhotoVerificationSettingsActionType.CLOCK_OUT,
  PositionPhotoVerificationSettingsActionType.START_BREAK,
  PositionPhotoVerificationSettingsActionType.END_BREAK,
  PositionPhotoVerificationSettingsActionType.SWITCH,
];

export const photoVerificationSettingsFormdDfaultActions = [
  PositionPhotoVerificationSettingsActionType.CLOCK_IN,
  PositionPhotoVerificationSettingsActionType.CLOCK_OUT,
  PositionPhotoVerificationSettingsActionType.START_BREAK,
  PositionPhotoVerificationSettingsActionType.END_BREAK,
];

export const photoVerificationSettingsFormScans = [
  PositionPhotoVerificationPPESettingsScanType.FACE_COVER,
  PositionPhotoVerificationPPESettingsScanType.HEAD_COVER,
  PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST,
  PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION,
];

export function getPhotoVerificationSettingsFormActionKey(action: PositionPhotoVerificationSettingsActionType) {
  switch (action) {
    case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
      return 'clockIn';
    case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
      return 'clockOut';
    case PositionPhotoVerificationSettingsActionType.START_BREAK:
      return 'startBreak';
    case PositionPhotoVerificationSettingsActionType.END_BREAK:
      return 'endBreak';
    case PositionPhotoVerificationSettingsActionType.SWITCH:
      return 'switch';
  }
}

export function getPhotoVerificationSettingsFormActionTitle(action: PositionPhotoVerificationSettingsActionType) {
  switch (action) {
    case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
      return t('Clock In');
    case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
      return t('Clock Out');
    case PositionPhotoVerificationSettingsActionType.START_BREAK:
      return t('Start Break');
    case PositionPhotoVerificationSettingsActionType.END_BREAK:
      return t('End Break');
    case PositionPhotoVerificationSettingsActionType.SWITCH:
      return t('Switch');
  }
}

export function getPhotoVerificationSettingsFormScanKey(action: PositionPhotoVerificationPPESettingsScanType) {
  switch (action) {
    case PositionPhotoVerificationPPESettingsScanType.FACE_COVER:
      return 'faceCover';
    case PositionPhotoVerificationPPESettingsScanType.HEAD_COVER:
      return 'headCover';
    case PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST:
      return 'safetyVest';
    case PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION:
      return 'eyeProtection';
  }
}

export function getPhotoVerificationSettingsFormScanTitle(action: PositionPhotoVerificationPPESettingsScanType) {
  switch (action) {
    case PositionPhotoVerificationPPESettingsScanType.FACE_COVER:
      return t('Face Cover');
    case PositionPhotoVerificationPPESettingsScanType.HEAD_COVER:
      return t('Head Cover');
    case PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST:
      return t('Safety Vest');
    case PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION:
      return t('Eye Protection');
  }
}

export function updatePPESettings(
  position: IPosition,
  isPPEEnabled: boolean,
  data: {
    clockInPPE: boolean;
    clockOutPPE: boolean;
    startBreakPPE: boolean;
    endBreakPPE: boolean;
    switchPPE: boolean;
  },
  scanData: {
    faceCover: boolean;
    headCover: boolean;
    safetyVest: boolean;
    eyeProtection: boolean;
  }
): IPhotoVerificationPPESettings[] {
  let ppeSettings = getPhotoVerificationSettings(position).ppe ?? defaultPositionPPESettings();

  if (isPPEEnabled) {
    const newScans = [
      scanData.faceCover ? PositionPhotoVerificationPPESettingsScanType.FACE_COVER : null,
      scanData.headCover ? PositionPhotoVerificationPPESettingsScanType.HEAD_COVER : null,
      scanData.safetyVest ? PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST : null,
      scanData.eyeProtection ? PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION : null,
    ].filter((scan) => scan !== null);

    ppeSettings = ppeSettings.filter(
      (setting) => setting.type !== PositionPhotoVerificationSettingsActionType.CLOCK_IN
    );
    ppeSettings.push({
      type: PositionPhotoVerificationSettingsActionType.CLOCK_IN,
      enabled: data.clockInPPE,
      scans: newScans,
    });

    ppeSettings = ppeSettings.filter(
      (setting) => setting.type !== PositionPhotoVerificationSettingsActionType.CLOCK_OUT
    );
    ppeSettings.push({
      type: PositionPhotoVerificationSettingsActionType.CLOCK_OUT,
      enabled: data.clockOutPPE,
      scans: newScans,
    });

    ppeSettings = ppeSettings.filter(
      (setting) => setting.type !== PositionPhotoVerificationSettingsActionType.START_BREAK
    );
    ppeSettings.push({
      type: PositionPhotoVerificationSettingsActionType.START_BREAK,
      enabled: data.startBreakPPE,
      scans: newScans,
    });

    ppeSettings = ppeSettings.filter(
      (setting) => setting.type !== PositionPhotoVerificationSettingsActionType.END_BREAK
    );
    ppeSettings.push({
      type: PositionPhotoVerificationSettingsActionType.END_BREAK,
      enabled: data.endBreakPPE,
      scans: newScans,
    });

    ppeSettings = ppeSettings.filter((setting) => setting.type !== PositionPhotoVerificationSettingsActionType.SWITCH);
    ppeSettings.push({
      type: PositionPhotoVerificationSettingsActionType.SWITCH,
      enabled: data.switchPPE,
      scans: newScans,
    });
  }

  return ppeSettings;
}

export function useIsPPEScanTypeEnabled() {
  const isFaceCoverEnabled = usePhotoVerificationPpeFaceCoverEnabled();
  const isHeadCoverEnabled = usePhotoVerificationPpeHeadCoverEnabled();
  const isSafetyVestEnabled = usePhotoVerificationPpeSafetyVestEnabled();
  const isEyeProtectionEnabled = usePhotoVerificationPpeEyeProtectionEnabled();

  return useCallback(
    (type: PositionPhotoVerificationPPESettingsScanType): boolean => {
      switch (type) {
        case PositionPhotoVerificationPPESettingsScanType.FACE_COVER:
          return isFaceCoverEnabled;
        case PositionPhotoVerificationPPESettingsScanType.HEAD_COVER:
          return isHeadCoverEnabled;
        case PositionPhotoVerificationPPESettingsScanType.SAFETY_VEST:
          return isSafetyVestEnabled;
        case PositionPhotoVerificationPPESettingsScanType.EYE_PROTECTION:
          return isEyeProtectionEnabled;
        default:
          return false;
      }
    },
    [isFaceCoverEnabled, isHeadCoverEnabled, isSafetyVestEnabled, isEyeProtectionEnabled]
  );
}
